/* eslint-disable */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import livekitService from '../services/livekitService';
// import { DEFAULT_VALUES } from '../constants';
import { Track } from 'livekit-client'; // --- ADD THIS IMPORT ---

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [authData, setAuthData] = useState({
    isAuthenticated: false,
    sessionId: null,
    roomName: null,
    livekitToken: null,
    livekitUrl: null,
    userIdentity: null,
    appId: null,
    apiKey: null,
    expiresIn: 0
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [voiceConnected, setVoiceConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [participants, setParticipants] = useState([]);

  // --- THIS IS THE FIRST NEW FUNCTION ---
  const handleTrackSubscribed = (track, publication, participant) => {
      // We only care about Alexander Burnett's audio track (which is remote)
  if (track.kind === Track.Kind.Audio && !participant.isLocal) {
    console.log(`🔊 Alexander Burnett voice connected from ${participant.identity}. Attaching audio element...`);
    // This creates and manages the <audio> element for you.
    // It will be attached to the document body where the browser can play it.
    track.attach();
    console.log('✅ Audio track attached. You should now hear Alexander Burnett.');
  }
  };

  // --- THIS IS THE SECOND NEW FUNCTION ---
  const handleTrackUnsubscribed = (track) => {
    // This correctly removes the audio element when the track is gone.
    track.detach().forEach((element) => element.remove());
  };

  // Login function
  const login = async (userIdentity, appId, apiKey) => {
    setLoading(true);
    setError(null);

    try {
      if (!appId || appId.trim() === '') throw new Error('App ID is required for authentication');
      if (!userIdentity || userIdentity.trim() === '') throw new Error('User identity is required');
      if (!apiKey || apiKey.trim() === '') throw new Error('API key is required');
      if (appId.trim().length < 10) throw new Error('Invalid App ID format');

      const response = await apiService.login(userIdentity, appId, apiKey);
      
      setAuthData({
        isAuthenticated: true,
        sessionId: response.session_id,
        roomName: response.room_name,
        livekitToken: response.livekit_token,
        livekitUrl: response.livekit_url,
        userIdentity: response.user_identity,
        appId: response.app_id,
        apiKey: apiKey,
        expiresIn: response.expires_in
      });

      localStorage.setItem('authData', JSON.stringify({
        sessionId: response.session_id,
        userIdentity: response.user_identity,
        appId: response.app_id,
        apiKey: apiKey
      }));

      console.log('✅ Authentication successful');
      
      if (response.livekit_url && response.livekit_token) {
        console.log('🔗 AuthContext: Auto-connecting to voice after login...');
        try {
          await connectToVoiceWithData(response.livekit_url, response.livekit_token, response.room_name);
        } catch (voiceError) {
          console.warn('⚠️ AuthContext: Auto voice connection failed:', voiceError);
        }
      }
      
      return response;

    } catch (error) {
      setError(error.message);
      console.error('❌ Authentication failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // LiveKit Voice Connection Functions
  const connectToVoiceWithData = async (livekitUrl, livekitToken, roomName) => {
    try {
      console.log('🔗 AuthContext: Connecting to voice with data...', { livekitUrl, roomName, hasToken: !!livekitToken });

      // Set up LiveKit event handlers
      livekitService.setOnConnectionStateChange((state) => {
        console.log('🔄 AuthContext: Connection state changed to:', state);
        setConnectionState(state);
        setVoiceConnected(state === 'connected');
      });

      livekitService.setOnParticipantConnected((participant) => {
        console.log('👤 AuthContext: Participant connected:', participant.identity);
        setParticipants(prev => [...prev, participant]);
      });

      livekitService.setOnParticipantDisconnected((participant) => {
        console.log('👤 AuthContext: Participant disconnected:', participant.identity);
        setParticipants(prev => prev.filter(p => p.identity !== participant.identity));
      });

      // --- ADD THESE LINES ---
      livekitService.setOnTrackSubscribed(handleTrackSubscribed);
      livekitService.setOnTrackUnsubscribed(handleTrackUnsubscribed);

      await livekitService.connect(livekitUrl, livekitToken, roomName);

      setVoiceConnected(true);
      console.log('✅ AuthContext: Voice connected successfully');
      console.log('🔇 AuthContext: Mic will remain muted until user unmutes');
      
    } catch (error) {
      console.error('❌ AuthContext: Voice connection failed:', error);
      setVoiceConnected(false);
      throw error;
    }
  };

  const connectToVoice = async () => {
    try {
      console.log('🔗 AuthContext: Connecting to voice...', { livekitUrl: authData.livekitUrl, roomName: authData.roomName, hasToken: !!authData.livekitToken });

      // Set up LiveKit event handlers
      livekitService.setOnConnectionStateChange((state) => {
        console.log('🔄 AuthContext: Connection state changed to:', state);
        setConnectionState(state);
        setVoiceConnected(state === 'connected');
      });

      livekitService.setOnParticipantConnected((participant) => {
        console.log('👤 AuthContext: Participant connected:', participant.identity);
        setParticipants(prev => [...prev, participant]);
      });

      livekitService.setOnParticipantDisconnected((participant) => {
        console.log('👤 AuthContext: Participant disconnected:', participant.identity);
        setParticipants(prev => prev.filter(p => p.identity !== participant.identity));
      });

      // --- ADD THESE LINES HERE AS WELL ---
      livekitService.setOnTrackSubscribed(handleTrackSubscribed);
      livekitService.setOnTrackUnsubscribed(handleTrackUnsubscribed);

      await livekitService.connect(authData.livekitUrl, authData.livekitToken, authData.roomName);

      setVoiceConnected(true);
      console.log('✅ AuthContext: Voice connected successfully');
      console.log('🔇 AuthContext: Mic will remain muted until user unmutes');
      
    } catch (error) {
      console.error('❌ AuthContext: Voice connection failed:', error);
      setVoiceConnected(false);
      throw error;
    }
  };

  // ... (The rest of the file: disconnectFromVoice, enableMicrophone, disableMicrophone, logout, etc. remains unchanged) ...
  const disconnectFromVoice = async () => {
    try {
      console.log('🔗 AuthContext: Disconnecting from voice...');
      await livekitService.disconnect();
      setVoiceConnected(false);
      setParticipants([]);
      console.log('✅ AuthContext: Voice disconnected successfully');
    } catch (error) {
      console.error('❌ AuthContext: Voice disconnect failed:', error);
      throw error;
    }
  };

  const enableMicrophone = async () => {
    try {
      console.log('🎤 AuthContext: Enabling microphone...');
      await livekitService.enableMicrophone();
      console.log('✅ AuthContext: Microphone enabled');
    } catch (error) {
      console.error('❌ AuthContext: Microphone enable failed:', error);
      throw error;
    }
  };

  const disableMicrophone = async () => {
    try {
      console.log('🎤 AuthContext: Disabling microphone...');
      await livekitService.disableMicrophone();
      console.log('✅ AuthContext: Microphone disabled');
    } catch (error) {
      console.error('❌ AuthContext: Microphone disable failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    console.log('🔐 AuthContext: Logging out...');
    if (voiceConnected) {
      try {
        await disconnectFromVoice();
      } catch (error) {
        console.warn('⚠️ AuthContext: Error disconnecting voice during logout:', error);
      }
    }
    setAuthData({
      isAuthenticated: false,
      sessionId: null,
      roomName: null,
      livekitToken: null,
      livekitUrl: null,
      userIdentity: null,
      appId: null,
      apiKey: null,
      expiresIn: 0
    });
    localStorage.removeItem('authData');
    setError(null);
    setVoiceConnected(false);
    setParticipants([]);
    console.log('🔓 Logged out');
  };

  const checkAuthStatus = async () => {
    if (!authData.apiKey) return false;
    try {
      const status = await apiService.getAuthStatus(authData.apiKey);
      if (status.is_active) {
        setAuthData(prev => ({ ...prev, expiresIn: status.expires_in }));
        return true;
      } else {
        logout();
        return false;
      }
    } catch (error) {
      console.error('❌ Auth status check failed:', error);
      logout();
      return false;
    }
  };

  useEffect(() => {
    const savedAuth = localStorage.getItem('authData');
    if (savedAuth) {
      try {
        const parsed = JSON.parse(savedAuth);
        setAuthData(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('❌ Failed to load saved auth data:', error);
        localStorage.removeItem('authData');
      }
    }
  }, []);

  // Memoize the checkAuthStatus function to prevent unnecessary re-renders
  const memoizedCheckAuthStatus = useCallback(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  useEffect(() => {
    if (authData.isAuthenticated) {
      const interval = setInterval(memoizedCheckAuthStatus, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [authData.isAuthenticated, memoizedCheckAuthStatus]);

  // Astrology login function
  const astroLogin = async (sessionInfo) => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔮 Starting astrology authentication...');

      setAuthData({
        isAuthenticated: true,
        sessionId: sessionInfo.session_id,
        roomName: sessionInfo.room_name,
        livekitToken: sessionInfo.livekit_token,
        livekitUrl: sessionInfo.livekit_url,
        userIdentity: sessionInfo.user_identity,
        appId: null,
        apiKey: sessionInfo.api_key,
        expiresIn: sessionInfo.expires_in
      });

      localStorage.setItem('authData', JSON.stringify({
        sessionId: sessionInfo.session_id,
        userIdentity: sessionInfo.user_identity,
        apiKey: sessionInfo.api_key
      }));

      console.log('✅ Astrology authentication successful');

      if (sessionInfo.livekit_url && sessionInfo.livekit_token) {
        console.log('🔗 AuthContext: Auto-connecting to astrology voice...');
        try {
          await connectToVoiceWithData(sessionInfo.livekit_url, sessionInfo.livekit_token, sessionInfo.room_name);
        } catch (voiceError) {
          console.warn('⚠️ AuthContext: Astrology voice connection failed:', voiceError);
        }
      }

      return sessionInfo;

    } catch (error) {
      setError(error.message);
      console.error('❌ Astrology authentication failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    ...authData,
    loading,
    error,
    voiceConnected,
    connectionState,
    participants,
    login,
    astroLogin,
    logout,
    checkAuthStatus,
    connectToVoice,
    connectToVoiceWithData,
    disconnectFromVoice,
    enableMicrophone,
    disableMicrophone
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};